import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { Card, CardHeader, ListGroup, ListGroupItem } from 'reactstrap';
import ButtonToolbar from 'src/componentes/button-toolbar';
import Button from 'src/componentes/button';
import Dialog from 'src/componentes/dialog';
import LoadingContainer from 'src/componentes/loading-container';
import ContentManager from 'src/componentes/content-manager';
import Alert from 'src/componentes/message-box/alert';
import Confirm from 'src/componentes/message-box/confirm';
import Tabs from 'src/componentes/tabs/tabs';
import Tab from 'src/componentes/tabs/tab';
import useAuth from 'src/hooks/useAuth';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';
import { capitalizeFirstLetter } from 'src/utils/string';
import focusManager from 'src/utils/focus-manager';
import { getZIndexAboveDialog } from 'src/utils/zIndexOverlay';
import ConfiguracoesTab from './Tabs/ConfiguracoesTab';
import PermissoesTab from './Tabs/PermissoesTab';
import RelacionamentosTab from './Tabs/RelacionamentosTab';
import ResumoTab from './Tabs/ResumoTab';
import VersoesTab from './Tabs/VersoesTab';
import {
  obterPublicoInteressadoDocumento,
  salvarPublicoInteressadoDocumento,
} from './Components/publicoInteressadoDocumento';
import { validarConfiguracaoLeitura } from './Components/configuracaoLeituraDocumento';
import {
  COPIA_CONTROLADA_FIELDS,
  TIPO_COPIA_DOCUMENTO,
} from './Components/copiaControladaDocumentoConstants';
import { STATUS_GERAL_DOCUMENTO, TIPO_COLABORADOR } from 'src/utils/constants';
import CriticalConfirm from 'src/componentes/message-box/critical-confirm';
import StatusBadge from 'src/componentes/StatusBadge';
import { useSelector } from 'react-redux';

function DocumentoEdit({
  item,
  indexItem,
  endpoint,
  parameters,
  documentType,
  documentName,
  handleClose,
  disabled,
  isDocument,
  initialTabIndex = 0,
  ...rest
}) {
  const defaultItem = {
    periodicidade: null,
    dataInicio: null,
    dataTermino: null,
    utilizarRecorrencia: false,
    excluido: false,
    editado: true,
    permissions: {
      allowEdit: true,
      allowDelete: true,
      allowView: true,
      allowRevert: true,
    },
  };

  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const modalRef = useRef();
  const closeConfirmationOpenRef = useRef(false);
  const { terms: resources, user } = useAuth();
  const [isNew, setIsNew] = useState(item == null);
  const [model, setModel] = useState(item == null ? defaultItem : item);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErros] = useState([]);
  const [hasChange, setHasChange] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasPublicoSaveError, setHasPublicoSaveError] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(initialTabIndex);
  const [versoesAfetadasCancelamento, setVersoesAfetadasCancelamento] =
    useState([]);
  const [statusGeralPersistidoId, setStatusGeralPersistidoId] = useState(
    item?.statusGeral?.id ?? null
  );
  const [isLoadingReverterCancelamento, setIsLoadingReverterCancelamento] =
    useState(false);

  const tipoColaboradorAtual = useSelector(
    (state) => state.user.tipoColaboradorAtual
  );

  const isExterno = tipoColaboradorAtual?.id === TIPO_COLABORADOR.EXTERNO;

  const isCurrentDocument =
    typeof isDocument === 'boolean' ? isDocument : !model?.agrupador;

  const ehDocumentoCancelado =
    statusGeralPersistidoId == STATUS_GERAL_DOCUMENTO.CANCELADO;

  const ehDocumentoReprovado =
    statusGeralPersistidoId == STATUS_GERAL_DOCUMENTO.REPROVADO;

  const emFluxoAprovacao = !!model?.emFluxoAprovacao;

  const aguardandoNovaSubmissao =
    statusGeralPersistidoId ==
    STATUS_GERAL_DOCUMENTO.REPROVADO_AGUARDANDO_NOVA_SUBMISSAO;

  const isBaseDisabled = disabled || !model?.permissions?.allowEdit;

  const isDisabled =
    isBaseDisabled || emFluxoAprovacao || (ehDocumentoCancelado && !isExterno);

  const isStatusGeralDisabled =
    isBaseDisabled ||
    emFluxoAprovacao ||
    aguardandoNovaSubmissao ||
    (isCurrentDocument && !model?.tipoDocumento?.id) ||
    (isNew &&
      isCurrentDocument &&
      model?.tipoDocumento?.fluxoAprovacaoCadastroDocumentoObrigatorio);

  const showExtendedTabs =
    !isNew &&
    !!model?.id &&
    isCurrentDocument &&
    !emFluxoAprovacao &&
    !aguardandoNovaSubmissao;

  const loadData = async () => {
    try {
      setIsLoading(true);
      setHasPublicoSaveError(false);
      const documentoId = item?.id || model?.id;

      if (documentoId) {
        const response = await http.post(`${endpoint}/Edit`, {
          id: documentoId,
          parameters,
        });
        const publicoInteressado = await obterPublicoInteressadoDocumento(
          response.data
        );
        setModel({ ...response.data, ...publicoInteressado });
        setStatusGeralPersistidoId(response.data?.statusGeral?.id ?? null);
        setHasUnsavedChanges(false);
      } else {
        const response = await http.post(`${endpoint}/Create`, {
          parameters,
        });
        setModel({
          ...response.data,
          statusGeral: response.data?.statusGeral ?? null,
        });
        setStatusGeralPersistidoId(response.data?.statusGeral?.id ?? null);
        setHasUnsavedChanges(false);
      }
    } catch (err) {
      errorHandler(err);
      handleClose && handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  const refreshVersionRelatedFields = async () => {
    const documentoId = item?.id || model?.id;
    if (!documentoId) return;

    try {
      const response = await http.post(`${endpoint}/Edit`, {
        id: documentoId,
        parameters,
      });

      const remoteModel = response?.data || {};
      setModel((current) => ({
        ...current,
        statusAtual: remoteModel?.statusAtual ?? null,
        statusProximaVersao: remoteModel?.statusProximaVersao ?? null,
        status: remoteModel?.status ?? null,
        arquivos: Array.isArray(remoteModel?.arquivos)
          ? remoteModel.arquivos
          : [],
        ultimoVersionamentoManual:
          remoteModel?.ultimoVersionamentoManual ?? null,
        bloquearDownload:
          typeof remoteModel?.bloquearDownload === 'boolean'
            ? remoteModel.bloquearDownload
            : current?.bloquearDownload,
      }));
    } catch (err) {
      errorHandler(err);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!showExtendedTabs && activeTabIndex !== 0) {
      setActiveTabIndex(0);
    }
  }, [activeTabIndex, showExtendedTabs]);

  useEffect(() => {
    if (
      model?.bloquearDownload &&
      (!model?.bloquearDownloadAposVencimentoDocumento ||
        !model?.bloquearDownloadAposConclusaoDocumento)
    ) {
      setModel((current) => ({
        ...current,
        bloquearDownloadAposVencimentoDocumento: true,
        bloquearDownloadAposConclusaoDocumento: true,
      }));
    }
  }, [model?.bloquearDownload]);

  const obterVersoesAfetadasCancelamento = async () => {
    try {
      const response = await http.post(
        `${endpoint}/ObterVersoesAfetadasCancelamento`,
        {
          documentoId: model?.id,
        }
      );

      setVersoesAfetadasCancelamento(response.data || []);
    } catch (err) {
      errorHandler(err);
    }
  };

  useEffect(() => {
    if (
      model?.statusGeral?.id == STATUS_GERAL_DOCUMENTO.CANCELADO &&
      !isDisabled
    ) {
      obterVersoesAfetadasCancelamento();
    }
  }, [model.statusGeral?.id]);

  const handleCurrentClose = (isSaved = false) => {
    modalRef.current?.close();
    setTimeout(() => {
      handleClose && handleClose(isSaved || hasChange, isNew, model, indexItem);
    }, 300);
  };

  const handleCloseRequest = () => {
    if (!hasUnsavedChanges) {
      handleCurrentClose();
      return;
    }
    if (closeConfirmationOpenRef.current) return;
    closeConfirmationOpenRef.current = true;

    ContentManager.addContent(
      <Confirm
        message={intl.formatMessage({
          id: 'msgConfirmarDescarteAlteracoesDocumento',
        })}
        labelConfirm={intl.formatMessage({ id: 'descartar' })}
        handleConfirm={() => handleCurrentClose()}
        onClose={() => {
          closeConfirmationOpenRef.current = false;
        }}
        zIndex={getZIndexAboveDialog()}
        focusTrapPriority={focusManager.getTopPriority() + 1}
      />
    );
  };

  const validateRequiredFields = () => {
    const fieldsWithError = [];

    !model?.nome && fieldsWithError.push('nome');
    !model?.idIntegracao &&
      (!model?.utilizarCodigoAutomatico ||
        (model?.utilizarCodigoAutomatico && !isNew)) &&
      fieldsWithError.push('idIntegracao');
    !model?.tipoDocumento &&
      isCurrentDocument &&
      fieldsWithError.push('tipoDocumento');
    !model?.area && fieldsWithError.push('area');
    !model?.statusGeral && fieldsWithError.push('statusGeral');
    (!model?.responsaveis || model?.responsaveis?.length === 0) &&
      fieldsWithError.push('responsaveis');
    model?.[COPIA_CONTROLADA_FIELDS.TIPO_COPIA_DOCUMENTO_ID] ===
      TIPO_COPIA_DOCUMENTO.COPIA_CONTROLADA &&
      Number(model?.[COPIA_CONTROLADA_FIELDS.QUANTIDADE_PERMITIDA_COPIAS]) <=
        0 &&
      fieldsWithError.push(COPIA_CONTROLADA_FIELDS.QUANTIDADE_PERMITIDA_COPIAS);

    return [...fieldsWithError, ...validarConfiguracaoLeitura(model)];
  };

  const handleSaveClick = async (
    concluir = false,
    reverter = false,
    fecharModal = false
  ) => {
    const fieldsWithError = validateRequiredFields();
    setErros(fieldsWithError);

    if (fieldsWithError.length > 0) {
      ContentManager.addContent(
        <Alert
          message={intl.formatMessage({
            id: 'label.osCamposDestacadosEmVermelhoSaoObrigatoriosFavorPreenchelos',
          })}
        />
      );
      return;
    }

    try {
      setIsLoading(true);
      const modelToSave = { ...model, agrupador: !isCurrentDocument };

      const response = await http.post(`${endpoint}/Save`, {
        model: modelToSave,
        parameters: { ...parameters, concluir, reverter },
      });

      setHasChange(true);
      const savedModel = response.data.model;
      setModel({
        ...savedModel,
        criteriosPublicoInteressado: modelToSave.criteriosPublicoInteressado,
      });
      setStatusGeralPersistidoId(savedModel.statusGeral?.id ?? null);
      setIsNew(false);
      setHasUnsavedChanges(true);

      let publicoInteressado;
      try {
        publicoInteressado = await salvarPublicoInteressadoDocumento(
          savedModel,
          modelToSave
        );
      } catch {
        setHasPublicoSaveError(true);
        ContentManager.addContent(
          <Alert
            message={intl.formatMessage({
              id: 'msgDocumentoSalvoCriteriosPublicoNaoSalvos',
            })}
          />
        );
        return;
      }

      setModel({ ...savedModel, ...publicoInteressado });
      setHasUnsavedChanges(false);
      setHasPublicoSaveError(false);
      ContentManager.addContent(
        <Alert
          title={intl.formatMessage({ id: 'sucesso' })}
          message={intl.formatMessage({
            id: 'label.registroSalvoComSucesso',
          })}
        />
      );

      if (fecharModal) {
        handleCurrentClose(true);
      }
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStatus = (status) => {
    return (
      <StatusBadge
        className="flex-shrink-0 mr-3"
        backgroundColor={status.corFundo}
        textColor={status.cor}
        text={status.nome}
      />
    );
  };

  const handleIntermediarySave = (
    concluir = false,
    reverter = false,
    fecharModal = false
  ) => {
    if (model.statusGeral?.id == STATUS_GERAL_DOCUMENTO.CANCELADO) {
      ContentManager.addContent(
        <CriticalConfirm
          title={intl.formatMessage({ id: 'labelCancelandoDocumento' })}
          textCapitalizeTitle={false}
          ignoreResourceTitle={true}
          styleTitle={{ textTransform: 'none' }}
          labelCheck={intl.formatMessage({ id: 'labelEstouCienteDestaAcao' })}
          labelConfirm={intl.formatMessage({ id: 'label.confirmar' })}
          handleConfirm={() => handleSaveClick(concluir, reverter, fecharModal)}
          width={900}
          message={
            <div>
              <div>
                <label>
                  {intl.formatMessage({
                    id: 'labelConfirmarCancelamentoDocumento',
                  })}
                </label>
                {versoesAfetadasCancelamento.length > 0 && (
                  <Card>
                    <CardHeader>
                      {intl.formatMessage({ id: 'labelVersoesAfetadas' })}
                    </CardHeader>
                    <ListGroup flush>
                      {versoesAfetadasCancelamento.map((versao) => (
                        <ListGroupItem key={versao.id}>
                          {versao.codigo} - {versao.dataVigencia} -{' '}
                          {renderStatus(versao.status)}
                        </ListGroupItem>
                      ))}
                    </ListGroup>
                  </Card>
                )}
              </div>
            </div>
          }
        />
      );

      return;
    }

    handleSaveClick(concluir, reverter, fecharModal);
  };

  const handleEnviarParaAprovacao = async () => {
    const fieldsWithError = validateRequiredFields();

    if (fieldsWithError.length > 0) {
      setErros(fieldsWithError);
      ContentManager.addContent(
        <Alert
          message={intl.formatMessage({
            id: 'label.osCamposDestacadosEmVermelhoSaoObrigatoriosFavorPreenchelos',
          })}
        />
      );
      return;
    }

    try {
      setIsLoading(true);
      const isNewRequest = isNew && !model?.id;
      await http.post(`${endpoint}/EnviarParaAprovacao`, {
        model: { ...model },
        parameters: { enviarParaAprovacao: true },
        isNew: isNewRequest,
      });

      enqueueSnackbar(
        intl.formatMessage({ id: 'labelEnvioDocumentoAprovacaoSucesso' }),
        {
          variant: 'success',
        }
      );

      handleCurrentClose(true);
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsLoading(false);
    }
  };

  const reverterCancelamento = async () => {
    try {
      setIsLoadingReverterCancelamento(true);
      await http.post(`${endpoint}/ReverterCancelamento`, {
        documentoId: model?.id,
      });
      await loadData();
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsLoadingReverterCancelamento(false);
    }
  };

  const handleChangeValue = (field, value) => {
    setHasUnsavedChanges(true);
    setModel((current) => ({ ...current, [field]: value }));
    setErros((current) => current.filter((error) => error !== field));
  };

  const hasErrorField = (field) => {
    return errors && errors.includes(field);
  };

  const handleOpenDocumentoAssociadoDetail = (itemDocumentoAssociado) => {
    const itemId = itemDocumentoAssociado?.id;
    const agrupador = itemDocumentoAssociado?.agrupador;

    if (!itemId) return;

    ContentManager.addContent(
      <DocumentoEdit
        item={{ id: itemId }}
        endpoint={endpoint}
        isDocument={!agrupador}
        disabled
      />
    );
  };

  const statusGeralFoiAlterado =
    model?.statusGeral?.id != statusGeralPersistidoId;

  const podeSalvarAlteracaoStatusDocumentoCancelado =
    ehDocumentoCancelado && statusGeralFoiAlterado && !isStatusGeralDisabled;

  const isSaveDisabled =
    isDisabled && !podeSalvarAlteracaoStatusDocumentoCancelado;

  const requiresApprovalOnCreate =
    isNew && model?.tipoDocumento?.fluxoAprovacaoCadastroDocumentoObrigatorio;

  const documentLabel = isCurrentDocument
    ? intl.formatMessage({ id: 'documento' })
    : intl.formatMessage({ id: 'pastaDocumentos' });

  const documentTitle = capitalizeFirstLetter(
    isCurrentDocument && model?.nome
      ? `${documentLabel} - ${model.nome}`
      : documentLabel
  );

  const actions = [
    <ButtonToolbar key="toolbar-tela">
      {ehDocumentoCancelado && !isExterno && !statusGeralFoiAlterado ? (
        <>
          <Button onClick={handleCloseRequest} color="secondary">
            <FormattedMessage id="label.cancelar" />
          </Button>
          <Button
            type="primary"
            disabled={isLoadingReverterCancelamento || disabled}
            onClick={() => reverterCancelamento()}
            htmlType="submit"
          >
            <FormattedMessage id="reverterCancelamento" />
          </Button>
        </>
      ) : (
        <>
          <Button onClick={handleCloseRequest} color="secondary">
            <FormattedMessage id="label.cancelar" />
          </Button>
          {!requiresApprovalOnCreate && (
            <Button
              type="primary"
              disabled={isSaveDisabled}
              onClick={() => handleIntermediarySave()}
              htmlType="submit"
            >
              <FormattedMessage id="label.salvar" />
            </Button>
          )}
          {!requiresApprovalOnCreate && (
            <Button
              type="primary"
              disabled={isSaveDisabled}
              onClick={() => handleIntermediarySave(false, false, true)}
              htmlType="submit"
            >
              <FormattedMessage id="label.salvarEFechar" />
            </Button>
          )}
          {(isNew
            ? model.tipoDocumento?.possuiFluxoAprovacaoCadastroDocumento
            : model.exibeBotaoEnviarParaAprovacao) && (
            <Button
              key="btnEnviarParaAprovacao"
              disabled={isDisabled}
              onClick={handleEnviarParaAprovacao}
            >
              {intl.formatMessage({ id: 'enviarParaAprovacao' })}
            </Button>
          )}
        </>
      )}
    </ButtonToolbar>,
  ];

  return (
    <Dialog
      id="documento-edit"
      title={documentTitle}
      ref={modalRef}
      width="90%"
      minWidth="80%"
      minHeight="60%"
      padContent
      actions={actions}
      {...rest}
      customHandleCloseFunction={handleCloseRequest}
      onRequestClose={handleCloseRequest}
    >
      <LoadingContainer isLoading={isLoading}>
        {hasPublicoSaveError && (
          <p
            role="alert"
            className="tw:mb-4 tw:rounded-md tw:border tw:border-destructive tw:bg-muted tw:p-3 tw:text-sm"
          >
            {intl.formatMessage({
              id: 'msgDocumentoSalvoCriteriosPublicoNaoSalvos',
            })}
          </p>
        )}
        <Tabs
          currenttabIndex={activeTabIndex}
          onSelect={setActiveTabIndex}
          styleContent={{ paddingTop: 16 }}
        >
          <Tab label={intl.formatMessage({ id: 'label.resumo' })}>
            <ResumoTab
              intl={intl}
              resources={resources}
              locale={user?.idioma}
              item={item}
              model={model}
              isNew={isNew}
              isCurrentDocument={isCurrentDocument}
              isDisabled={isDisabled}
              isDocumentTypeDisabled={aguardandoNovaSubmissao}
              isStatusGeralDisabled={isStatusGeralDisabled}
              documentName={documentName}
              documentType={documentType}
              hasErrorField={hasErrorField}
              onChangeValue={handleChangeValue}
            />
          </Tab>
          {showExtendedTabs && (
            <Tab label={intl.formatMessage({ id: 'versoes' })}>
              <VersoesTab
                model={model}
                documentName={documentName}
                documentType={documentType}
                disableCreate={ehDocumentoReprovado}
                onVersionSaved={refreshVersionRelatedFields}
              />
            </Tab>
          )}
          {showExtendedTabs && (
            <Tab label={intl.formatMessage({ id: 'label.relacionamentos' })}>
              <RelacionamentosTab
                intl={intl}
                model={model}
                endpoint={endpoint}
                disabled={disabled}
                disableSearch={ehDocumentoReprovado}
                isCurrentDocument={isCurrentDocument}
                isDisabled={isDisabled}
                onChangeValue={handleChangeValue}
                onOpenDocumentoAssociadoDetail={
                  handleOpenDocumentoAssociadoDetail
                }
              />
            </Tab>
          )}
          {showExtendedTabs && (
            <Tab label={intl.formatMessage({ id: 'configuracoes' })}>
              <ConfiguracoesTab
                intl={intl}
                model={model}
                isDisabled={isDisabled}
                onChangeValue={handleChangeValue}
                hasErrorField={hasErrorField}
                hasUnsavedChanges={hasUnsavedChanges}
                isActive={activeTabIndex === 3}
              />
            </Tab>
          )}
          {showExtendedTabs && (
            <Tab label={intl.formatMessage({ id: 'label.permissoes' })}>
              <PermissoesTab model={model} isDisabled={isDisabled} />
            </Tab>
          )}
        </Tabs>
      </LoadingContainer>
    </Dialog>
  );
}

DocumentoEdit.defaultProps = {
  endpoint: '/Documento',
};

export default DocumentoEdit;
